import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import type { JobStatus } from "@/lib/types";
import { money, relTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { StatusBadge } from "@/components/StatusBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface JobListItem {
  id: string;
  service_title: string;
  status: JobStatus;
  currency: string;
  quoted_price_cents: number | null;
  queue_position: number | null;
  created_at: number;
  updated_at: number;
  counterparty_handle: string | null;
  counterparty_name: string | null;
  counterparty_avatar: string | null;
}

function JobRow({ job, role }: { job: JobListItem; role: "client" | "creator" }) {
  return (
    <Link
      to={`/jobs/${job.id}`}
      className="group flex items-center gap-4 rounded-lg border bg-card p-4 transition hover:border-foreground/30 hover:shadow-sm"
    >
      <Avatar className="h-9 w-9">
        <AvatarImage src={job.counterparty_avatar ?? undefined} />
        <AvatarFallback>
          {(job.counterparty_name ?? job.counterparty_handle ?? "?").slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate font-medium">{job.service_title}</p>
          <StatusBadge status={job.status} />
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {role === "client" ? "with" : "from"} {job.counterparty_name ?? `@${job.counterparty_handle}`} · updated {relTime(job.updated_at)}
          {job.queue_position ? ` · queue #${job.queue_position}` : ""}
        </p>
      </div>
      <div className="hidden text-right md:block">
        <p className="tabular-nums">{money(job.quoted_price_cents, job.currency)}</p>
        <p className="text-xs text-muted-foreground">{relTime(job.created_at)}</p>
      </div>
      <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </Link>
  );
}

function JobList({ role }: { role: "client" | "creator" }) {
  const [jobs, setJobs] = useState<JobListItem[] | null>(null);
  useEffect(() => {
    (async () => {
      const res = await api.get<{ jobs: JobListItem[] }>(`/api/jobs/mine?as=${role}`);
      setJobs(res.jobs);
    })();
  }, [role]);

  if (!jobs) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }
  if (jobs.length === 0) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-sm text-muted-foreground">
          {role === "client"
            ? "No commissions yet. Browse creators to get started."
            : "No incoming requests yet. Share your fanfix link."}
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {jobs.map(j => <JobRow key={j.id} job={j} role={role} />)}
    </div>
  );
}

export function ClientDashboardPage() {
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Your commissions</h1>
          <p className="text-sm text-muted-foreground">
            Every project in one place. No more digging through DMs.
          </p>
        </div>
        <Button asChild variant="outline"><Link to="/discover">Find a creator</Link></Button>
      </div>

      {user?.is_creator ? (
        <Tabs defaultValue="client">
          <TabsList>
            <TabsTrigger value="client">As client</TabsTrigger>
            <TabsTrigger value="creator">As creator</TabsTrigger>
          </TabsList>
          <TabsContent value="client" className="mt-4"><JobList role="client" /></TabsContent>
          <TabsContent value="creator" className="mt-4"><JobList role="creator" /></TabsContent>
        </Tabs>
      ) : (
        <JobList role="client" />
      )}
    </div>
  );
}
